<template>
    <div class="details">
        <ProductDetailsHeader :product="product" class="details-header" />

        <LazyImage :src="product.ImageUrl" :alt="product.Description" class="details-image" />

        <div class="details-content">
            <p class="details-content-title">
                {{ product.Description }}
            </p>
            <p class="details-content-destination">
                {{ product.Destination }}
            </p>
        </div>

        <Rating v-if="product.Rating" :rating="product.Rating" class="details-rating" />

        <div class="details-dates">
            <div v-if="product.FromDate">
                <span>
                    <strong>
                        {{ t({ key: 'shared.check_in', suffix: ':' }) }}
                    </strong>
                    {{ onFormatDate(product.FromDate) }}
                </span>
            </div>
            <div v-if="product.ToDate">
                <span>
                    <strong>
                        {{ t({ key: 'shared.check_out', suffix: ':' }) }}
                    </strong>
                    {{ onFormatDate(product.ToDate) }}
                </span>
            </div>
        </div>

        <ButtonLink
            size="m"
            color="black"
            :onClick="onHotelDetailsClick"
            class="details-link">
            {{ t({ key: 'shared.stay_and_room_details' }) }}
        </ButtonLink>

        <HotelPrice :product="product" class="details-price" />

        <div class="details-cancellation">
            <CancellationPanel :product="product" />
        </div>

        <Notification
            v-if="hasImportantNotes"
            variant="warning"
            icon="InfoCircle"
            padding="l"
            :onLinkClick="onImportantInformationClick"
            :linkLabel="t({ key: 'shared.important_information' })"
            :tier="2"
            class="details-important" />

        <Divider v-if="!isDesktop" color="lightGrey" thickness="1" topMargin="xxl" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import { trackClarity } from '@/tracking/clarity';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import { HotelProduct } from '@/api/Cart/cart.interfaces';
    import { ProductModalTab, ModalType } from '@/store/general/types';
    import { useStore } from '@/store';
    import { getLocaleWeekdayLongMonthYear } from '@shared/helpers/datetime/datetime.formatters';
    import { convertToDate } from '@shared/helpers/datetime/datetime';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { capitalizeWords } from '@shared/utils/string';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { scrollToElementIntoContainer } from '@shared/helpers/scroll';
    import {
        logStatsigCheckoutImportantNotesOpen,
        logStatsigCheckoutMoreInformationOpen,
    } from '@/tracking/statsig/checkout.functions';
    import LazyImage from '@ui/components/LazyImage/LazyImage.vue';
    import Rating from '@ui/components/Rating/Rating.vue';
    import CancellationPanel from '@/components/checkoutPageV3/Shared/CancellationPanel/CancellationPanel.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import ProductDetailsHeader from '@/components/checkoutPageV3/Shared/ProductDetailsHeader/ProductDetailsHeader.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import HotelPrice from '@/components/checkoutPageV3/Sections/Products/Hotel/Details/Price/Price.vue';

    export default defineComponent({
        name: 'HotelDetails',
        methods: { t, getMoneyObjectAsString },
        components: {
            HotelPrice,
            ButtonLink,
            ProductDetailsHeader,
            LazyImage,
            Rating,
            CancellationPanel,
            Notification,
            Divider,
        },
        props: {
            product: {
                type: Object as PropType<HotelProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();
            const { isDesktop } = useScreenSize();

            const hasImportantNotes = computed(
                () => props.product.Rooms.flatMap((room) => room.AllNotes).length > 0
            );

            function onFormatDate(date: string) {
                return capitalizeWords(getLocaleWeekdayLongMonthYear(convertToDate(date)));
            }

            function onImportantInformationClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Details,
                    onOpen,
                });

                trackClarity(ClarityEvent.CHECKOUT_IMPORTANT_NOTES_OPEN);

                logStatsigCheckoutImportantNotesOpen();
            }

            function onOpen() {
                scrollToElementIntoContainer('modal-container', 'important-notes');
            }

            function onHotelDetailsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Details,
                });

                logStatsigCheckoutMoreInformationOpen(props.product.Type);
            }

            return {
                LabelFormat,
                hasImportantNotes,
                isDesktop,
                onFormatDate,
                onHotelDetailsClick,
                onImportantInformationClick,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Details.scss';
</style>
